import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  IconButton,
  LinearProgress,
  Chip,
  Collapse,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Stars as RewardsIcon,
  ReceiptLong as StatementsIcon,
  Archive as ArchiveIcon,
  Restore as RestoreIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalanceWalletOutlined as LimitIcon,
  RequestQuoteOutlined as BillIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/formatters';
import CardNetworkLogo from '../CardNetworkLogo';

// Helper function to format dates as DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
};

// Badge text and colour for each payment status computed by the page
const STATUS = {
  overdue: { label: 'Overdue', color: 'error' },
  unpaid: { label: 'Unpaid', color: 'error' },
  partially_paid: { label: 'Partially Paid', color: 'warning' },
  fully_paid: { label: 'Fully Paid', color: 'success' },
  no_payment_required: { label: 'No Payment Required', color: 'success' },
  no_bill: { label: 'No Bill Generated', color: 'default' },
  no_dates_set: { label: 'Set Bill Dates', color: 'warning' },
};

const utilizationColor = (utilization) =>
  utilization > 80 ? 'error.main' : utilization > 50 ? 'warning.main' : 'success.main';

const SectionTitle = ({ icon, children }) => (
  <Typography
    variant="overline"
    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', fontSize: '0.6875rem', lineHeight: 1.8, mb: 1.5 }}
  >
    {icon}
    {children}
  </Typography>
);

const Stat = ({ label, value, color = 'text.primary', align = 'left' }) => (
  <Box sx={{ textAlign: align, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 700, color, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' }} noWrap>
      {value}
    </Typography>
  </Box>
);

const DateRow = ({ label, value, color = 'text.primary' }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color, textAlign: 'right' }}>{value}</Typography>
  </Box>
);

const progressSx = (barColor, height = 8) => ({
  height,
  borderRadius: height / 2,
  bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
  '& .MuiLinearProgress-bar': { borderRadius: height / 2, bgcolor: barColor },
});

// One credit card: full three-column layout on desktop; on small screens a compact
// summary that expands to the full details (and actions) when tapped.
const CreditCardItem = ({ card, getMonthName, onEdit, onArchive, onRewards, onStatements }) => {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(false);
  const showDetails = !compact || expanded;

  const status = STATUS[card.paymentStatus] || STATUS.no_bill;
  const needsAttention = card.paymentStatus === 'unpaid' || card.paymentStatus === 'overdue';
  const isLinked = !card.isPrimary && card.primaryCardId;
  const remainingBill = Math.max(0, (card.billAmount || 0) - (card.paymentsMade || 0));

  const dueText = card.isPastDue
    ? `Overdue by ${Math.abs(card.daysUntilDue)} days`
    : card.daysUntilDue === 0
      ? 'Due today'
      : `${card.daysUntilDue} days until due`;
  const dueColor = card.isPastDue ? 'error.main' : card.daysUntilDue <= 3 ? 'warning.main' : 'info.main';

  // ---- Header: logo, name, badges, number, actions ----
  const header = (
    <Box sx={{ display: 'flex', alignItems: compact ? 'flex-start' : 'center', gap: { xs: 1.5, md: 2 }, flexWrap: { xs: 'nowrap', md: 'wrap' } }}>
      <CardNetworkLogo variant={card.card_variant} sx={{ boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.1)' }} />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, width: compact ? '100%' : 'auto' }}>
            {card.name}
          </Typography>
          {card.isPrimary && <Chip label="Primary" size="small" color="primary" variant="outlined" />}
          {isLinked && <Chip label="Linked" size="small" color="info" variant="outlined" />}
          {card.is_archived && <Chip label="Archived" size="small" />}
          <Chip label={status.label} size="small" color={status.color} sx={{ fontWeight: 600 }} />
        </Box>
        {card.account_number && (
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', letterSpacing: 1, mt: 0.25 }}>
            •••• {card.account_number.slice(-4)}
          </Typography>
        )}
      </Box>
      {compact ? (
        <ExpandMoreIcon sx={{ color: 'text.secondary', flexShrink: 0, alignSelf: 'flex-start', mt: 0.5, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none' }} />
      ) : (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
          <Button size="small" variant="outlined" startIcon={<RewardsIcon />} onClick={() => onRewards(card)} data-tour="cc-rewards-btn">Rewards</Button>
          <Button size="small" variant="outlined" startIcon={<StatementsIcon />} onClick={() => onStatements(card)} data-tour="cc-statements">Statements</Button>
          <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={() => onEdit(card)} data-tour="cc-edit">Edit Details</Button>
          <Tooltip title={card.is_archived ? 'Restore' : 'Archive'}>
            <IconButton
              size="small"
              onClick={() => onArchive(card)}
              sx={{ border: 1, borderColor: 'divider', color: card.is_archived ? 'success.main' : 'text.secondary' }}
              aria-label={card.is_archived ? 'Restore' : 'Archive'}
            >
              {card.is_archived ? <RestoreIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  // ---- Compact summary (mobile): limit + what's due ----
  const summary = (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary">Available</Typography>
        <Typography variant="caption" color="text.secondary">
          {Math.round(card.utilization || 0)}% used
        </Typography>
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 1, fontVariantNumeric: 'tabular-nums' }}>
        {formatCurrency(card.availableBalance)}
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.75 }}>
          of {formatCurrency(card.totalLimit)}
        </Typography>
      </Typography>
      <LinearProgress variant="determinate" value={Math.min(100, card.utilization || 0)} sx={progressSx(utilizationColor(card.utilization), 6)} />
      {card.billAmount > 0 && !card.isFullyPaid && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatCurrency(remainingBill)} due
          </Typography>
          {card.daysUntilDue !== undefined && (
            <Typography variant="body2" sx={{ color: dueColor, fontWeight: 600 }}>{dueText}</Typography>
          )}
        </Box>
      )}
    </Box>
  );

  // ---- Full details: three sections ----
  const limitSection = (
    <Box>
      <SectionTitle icon={<LimitIcon sx={{ fontSize: 16 }} />}>
        Credit Utilization{isLinked ? ' · Shared Limit' : ''}
      </SectionTitle>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.375rem', fontVariantNumeric: 'tabular-nums' }}>
          {formatCurrency(card.availableBalance)}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: utilizationColor(card.utilization) }}>
          {Math.round(card.utilization || 0)}% used
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={Math.min(100, card.utilization || 0)} sx={progressSx(utilizationColor(card.utilization))} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1.5, mt: 2 }}>
        <Stat label={card.isPrimary || !card.primaryCardId ? 'Used' : 'This Card Used'} value={formatCurrency(card.usedAmount)} color="error.main" />
        <Stat label="Available" value={formatCurrency(card.availableBalance)} color="success.main" />
        <Stat label="Limit" value={formatCurrency(card.totalLimit)} />
      </Box>
      {card.isPrimary && card.linkedCards?.length > 1 && (
        <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: (t) => alpha(t.palette.error.main, 0.05) }}>
          <Typography variant="caption" color="text.secondary">Total Used (All Cards)</Typography>
          <Typography sx={{ fontWeight: 700, color: 'error.main' }}>{formatCurrency(card.totalUsedLimit)}</Typography>
          <Typography variant="caption" color="text.secondary">Across {card.linkedCards.length} cards</Typography>
        </Box>
      )}
    </Box>
  );

  const billSection = (
    <Box data-tour="cc-bill">
      <SectionTitle icon={<BillIcon sx={{ fontSize: 16 }} />}>Bill Payment Status</SectionTitle>

      {(card.paymentStatus === 'no_bill' || card.paymentStatus === 'no_payment_required') && (
        <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, mb: 1 }}>
          {card.paymentStatus === 'no_payment_required' ? 'No Payment Required' : 'No Bill Generated'}
        </Typography>
      )}
      {card.paymentStatus === 'no_dates_set' && (
        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600, mb: 1 }}>
          Please set billing and due dates
        </Typography>
      )}

      {card.billAmount > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.25 }}>
            <Stat label="Bill Amount" value={formatCurrency(card.billAmount)} />
            <Stat label="Paid" value={formatCurrency(card.paymentsMade)} color="success.main" align="right" />
          </Box>
          <LinearProgress
            variant="determinate"
            value={card.paymentProgress}
            sx={progressSx(card.isFullyPaid ? 'success.main' : card.paymentProgress > 0 ? 'warning.main' : card.isPastDue ? 'error.main' : 'primary.main')}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: `${status.color === 'default' ? 'text.secondary' : `${status.color}.main`}` }}>
              {card.isFullyPaid ? '✓ Fully Paid' : status.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">{Math.round(card.paymentProgress)}%</Typography>
          </Box>
          {card.daysUntilDue !== undefined && !card.isFullyPaid && (
            <Typography variant="body2" sx={{ color: dueColor, fontWeight: 600, mt: 0.5 }}>{dueText}</Typography>
          )}
          {!card.isFullyPaid && card.billAmount > card.paymentsMade && (
            <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
              Remaining: {formatCurrency(card.billAmount - card.paymentsMade)}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 0.5 }}>
        {card.billAmount > 0 && <DateRow label="Next Due Date" value={formatDate(card.nextDueDate)} color="error.main" />}
        <DateRow label="Bill Date" value={card.billing_date ? `${card.billing_date} of every month` : '-'} />
        <DateRow label="Due Date" value={card.due_date ? `${card.due_date} of every month` : '-'} />
        <DateRow label="Anniversary" value={card.anniversary_month ? getMonthName(card.anniversary_month) : '-'} />
      </Box>
    </Box>
  );

  const rewardsSection = (
    <Box data-tour="cc-rewards">
      <SectionTitle icon={<RewardsIcon sx={{ fontSize: 16 }} />}>Rewards & Benefits</SectionTitle>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <Stat label="Annual Fee" value={formatCurrency(card.annual_fee || 0)} color="warning.main" />
        <Stat label="Spend Waiver" value={formatCurrency(card.spend_waiver || 0)} />
        <Stat label="Rewards Points" value={(card.rewards_points || 0).toLocaleString()} color="info.main" />
        <Stat label="Points Value" value={formatCurrency((card.rewards_points || 0) * (card.rewards_value || 0))} color="success.main" />
      </Box>

      {card.spend_waiver > 0 && (
        <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Progress to Annual Fee Waiver
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, card.spendWaiverProgress || 0)}
            sx={{ ...progressSx(card.spendWaiverProgress >= 100 ? 'success.main' : 'primary.main', 6), my: 0.75 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {formatCurrency(card.annualSpend)} / {formatCurrency(card.spend_waiver)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Math.min(100, Math.round(card.spendWaiverProgress || 0))}%
            </Typography>
          </Box>
          {card.initial_usage > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Includes initial usage: {formatCurrency(card.initial_usage)}
            </Typography>
          )}
          {card.remainingSpend > 0 && (
            <Typography variant="body2" color="error.main" sx={{ mt: 0.75 }}>
              {formatCurrency(card.remainingSpend)} more to waive annual fee
              {card.daysUntilAnniversary && card.daysUntilAnniversary > 0 && ` (in ${card.daysUntilAnniversary} days)`}
            </Typography>
          )}
          {card.spendWaiverProgress >= 100 && (
            <Typography variant="body2" color="success.main" sx={{ mt: 0.75 }}>
              Annual fee waiver achieved! 🎉
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  const mobileActions = (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 2.5 }}>
      <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={() => onEdit(card)} data-tour="cc-edit">Edit Details</Button>
      <Button size="small" variant="outlined" startIcon={<RewardsIcon />} onClick={() => onRewards(card)} data-tour="cc-rewards-btn">Rewards</Button>
      <Button size="small" variant="outlined" startIcon={<StatementsIcon />} onClick={() => onStatements(card)} data-tour="cc-statements">Statements</Button>
      <Button
        size="small"
        variant="outlined"
        color={card.is_archived ? 'success' : 'inherit'}
        startIcon={card.is_archived ? <RestoreIcon /> : <ArchiveIcon />}
        onClick={() => onArchive(card)}
      >
        {card.is_archived ? 'Restore' : 'Archive'}
      </Button>
    </Box>
  );

  return (
    <Card data-tour="cc-card" data-has-bill={card.billAmount > 0 ? 'true' : 'false'}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: 'var(--fx-card-shadow-hover)' },
        // Accent stripe for bills that need paying
        '&::before': needsAttention ? {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: 'error.main',
        } : undefined,
      }}
    >
      <Box
        onClick={compact ? () => setExpanded((v) => !v) : undefined}
        role={compact ? 'button' : undefined}
        aria-expanded={compact ? expanded : undefined}
        sx={{ p: { xs: 2, md: 3 }, pb: compact && !expanded ? 2 : { xs: 2, md: 3 }, cursor: compact ? 'pointer' : 'default' }}
      >
        {header}
        {compact && !expanded && summary}
      </Box>

      <Collapse in={showDetails} timeout="auto" unmountOnExit>
        {compact && <Divider />}
        <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, pt: compact ? 2 : 0 }}>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            <Grid item xs={12} md={4} sx={{ pr: { md: 3 } }}>{limitSection}</Grid>
            <Grid item xs={12} md={4} sx={{ pr: { md: 3 }, borderLeft: { md: 1 }, borderColor: { md: 'divider' } }}>{billSection}</Grid>
            <Grid item xs={12} md={4} sx={{ borderLeft: { md: 1 }, borderColor: { md: 'divider' } }}>{rewardsSection}</Grid>
          </Grid>
          {compact && mobileActions}
        </Box>
      </Collapse>
    </Card>
  );
};

export default CreditCardItem;
